import dayjs from 'dayjs';
import 'dayjs/locale/th';
import isEmpty from 'lodash/isEmpty';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import numeral from 'numeral';
import PageError from 'pages/404';
import { useState } from 'react';
import { BsCaretRight } from 'react-icons/bs';
import { FaEye, FaRegCalendarAlt } from 'src/components/icon';
import MediaQuery from 'react-responsive';
import DynamicAds, { KEY_ADS_POSITION } from 'src/components/ads/dynamic-ads';
import CardFull from 'src/components/cards-video/card-full';
import CardHVideo from 'src/components/cards-video/card-h';
import Layout from 'src/components/layouts';
import WidgetSocialShare from 'src/components/widgets/widget-social-share';
import { APP_IMG, APP_URL, IS_NO_ROBOT_INDEX, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_LOGO, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { escapeChar, isExistAds } from 'src/utils/helper';
import { GET } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';

const LineLink = dynamic(import('src/components/lineLink'));

const PageVideoDetail = ({ article, related, lastNews, ads }) => {
  const data = useUser();
  const sessionSevId = data?.user ? decryptionClient(data?.user) : null;
  if (isEmpty(article)) return <PageError sessionSevId={sessionSevId} />;
  const dateUpdate = dayjs(article?.published_at).locale('th').add(543, 'year').format('DD MMM YYYY');
  const articleLink = `/live/${article.section.en}/${article.id}`;
  const [defalutData, setDefalutData] = useState({
    video_type: '',
    video_src: ''
  });
  if (lastNews?.length > 0 && defalutData?.video_src === '') {
    setDefalutData({ video_type: lastNews[0]?.type_video, video_src: lastNews[0]?.video_src });
  }
  const router = useRouter();
  const baseUrlCategory = `${APP_URL}${router.asPath}`;
  /* ----------------------------------- SEO ---------------------------------- */
  const articleTitleSeo = escapeChar(article?.title) || WEB_TITLE;
  const articleDescriptionSeo = escapeChar(article?.description) || WEB_DESC;

  const getVideoIframe = () => {
    let urlVideo;
    if (article?.type_video === '0') {
      urlVideo = `${APP_IMG}/${article?.video_src}`;
    } else {
      urlVideo = article?.video_src;
    }
    return (
      <>
        {article?.type_video === '4' ? (
          <div dangerouslySetInnerHTML={urlVideo} />
        ) : (
          <iframe
            // className='video-frame'
            src={urlVideo}
            frameBorder='0'
          />
        )}
      </>
    );
  };

  return (
    <>
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={article?.title ? `${article?.title} - ขอบสนาม` : WEB_TITLE}
        description={article?.description || WEB_DESC}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          site_name: WEB_NAME,
          title: article?.title ? `${article?.title} - ขอบสนาม` : WEB_TITLE,
          description: article?.description || WEB_DESC,
          url: baseUrlCategory,
          article: {
            publishedTime: article?.published_at,
            modifiedTime: article?.updated_at,
            expirationTime: null,
            section: article?.section?.th,
            authors: [`${WEB_URL.FACEBOOK}`],
            tags: article?.tags
          },
          images: [
            {
              url: article?.image,
              width: '700',
              height: '395',
              alt: article?.title ? `${article?.title} - ขอบสนาม` : WEB_TITLE
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: article?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={article?.title ? `${article?.title} - ขอบสนาม` : WEB_TITLE} />
        <meta property='twitter:description' content={article?.description || WEB_DESC} />
        <meta property='twitter:image' content={article?.image_url || IMAGE_DEFAULT} />

        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                    __th_page="${article?.section?.en}";
                  `
          }}
        />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                    {
                      "@context": "https://schema.org",
                      "@type": "WebSite",
                      "name": "${WEB_NAME}",
                      "url": "${WEB_URL.WEB}",
                      "description": "${articleDescriptionSeo}",
                      "sameAs": [
                        "${WEB_URL.FACEBOOK}",
                        "${WEB_URL.YOUTUBE}",
                        "${WEB_URL.INSTAGRAM}"
                      ],
                      "potentialAction": {
                        "@type": "SearchAction",
                        "target": "${WEB_URL.WEB}search?q={search_term_string}",
                        "query-input": "required name=search_term_string"
                      }
                    }
                  `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "WebPage", 
                      "name": "${articleTitleSeo} - ขอบสนาม", 
                      "description": "${articleDescriptionSeo}", 
                      "publisher": { 
                        "@type": "ProfilePage", 
                        "name": "${WEB_NAME}" 
                      } 
                    } 
                  `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "BreadcrumbList", 
                      "itemListElement": [ 
                        { 
                          "@type": "ListItem", 
                          "position": 1, 
                          "name": "หน้าแรก", 
                          "item": "${APP_URL}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 2, 
                          "name": "${article?.section?.th}", 
                          "item": "${APP_URL}/${article?.section?.en}" 
                        }, 
                        { 
                          "@type": "ListItem", 
                          "position": 3, 
                          "name": "${articleTitleSeo} - ขอบสนาม", 
                          "item": "${article?.canonical || `${APP_URL}${article?.link}`}" 
                        } 
                      ] 
                    } 
                  `
          }}
        />

        {/* -------------------------- SNIPPET : NewsArticle ------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: ` 
                    { 
                      "@context": "http://schema.org", 
                      "@type": "NewsArticle", 
                      "mainEntityOfPage": { 
                        "@type": "WebPage", 
                        "@id": "${article?.canonical || `${APP_URL}${article?.link}`}" 
                      }, 
                      "headline": "${articleTitleSeo} - ขอบสนาม", 
                      "alternativeHeadline": "${articleTitleSeo} - ขอบสนาม", 
                      "description": "${articleDescriptionSeo}", 
                      "image": "${APP_IMG}${article?.image}", 
                      "datePublished": "${article?.published_at}",
                      "dateModified": "${article?.published_at}",
                      "author": { 
                        "@type": "Thing", 
                        "name": "${article.byline || WEB_NAME}",
                        "url": "${APP_URL}"
                      }, 
                      "publisher": { 
                        "@type": "Organization", 
                        "name": "${WEB_NAME}", 
                        "logo": { 
                          "@type": "ImageObject", 
                          "url": "${APP_IMG}/images/${WEB_LOGO}" 
                        } 
                      } 
                    } 
                  `
          }}
        />
      </Head>
      <Layout sessionSevId={sessionSevId}>
        {isExistAds(ads, KEY_ADS_POSITION.BILLBOARD) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}
        <CategoryContent className='container mb-5' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : '' }}>
          <div className='row mt-15'>
            <ul className='breadcrumb'>
              <li>
                <a
                  href='/'
                  //! DATALAYER
                  // onClick={() =>
                  //   sendDataLayerGlobal({
                  //     key: KEY_EVENT.TRACK_EVENT,
                  //     event: KEY_EVENT.BREADCRUMB,
                  //     data: {
                  //       title: 'หน้าแรก',
                  //       link: '/'
                  //     }
                  //   })
                  // }
                >
                  หน้าแรก
                </a>
              </li>

              <li>
                <a
                  href={`/live/${article?.section.en}`}
                  //! DATALAYER
                  // onClick={() =>
                  //   sendDataLayerGlobal({
                  //     key: KEY_EVENT.TRACK_EVENT,
                  //     event: KEY_EVENT.BREADCRUMB,
                  //     data: {
                  //       title: article?.section.th,
                  //       link: `/live/${article?.section.en}`
                  //     }
                  //   })
                  // }
                >
                  {article?.section.th}
                </a>
              </li>
            </ul>
            {/* {article?.section?.th && <h1 className='heading-title'>{article?.section?.th}</h1>} */}
          </div>

          <div className='row'>
            <div className='content-heading-wrapper'>
              <h1 className='content-title'>{article?.title}</h1>
              <div className='content-meta'>
                <span>
                  <FaRegCalendarAlt />
                  {dateUpdate}
                </span>
                {article?.pageviews > 0 && (
                  <span>
                    <FaEye />
                    {article?.pageviews > 999 ? numeral(article?.pageviews).format('0.0a') : article?.pageviews}
                  </span>
                )}
                <div className='justify-content-between align-items-center d-inline-flex custom-socail'>
                  <span className='me-1 d-none d-lg-flex'>แชร์ | </span>
                  <WidgetSocialShare
                    articleLink={articleLink}
                    //! DATALAYER
                    // eventDataLayer={{
                    //   key: KEY_EVENT.PAGE_SECTION,
                    //   event: KEY_EVENT.SOCIAL_SHARE,
                    //   section: 'section-1'
                    // }}
                  />
                </div>
              </div>
            </div>
            <div className='col-12 col-lg-8'>
              <div className='video-player article-video-player'>{getVideoIframe()}</div>
              {isExistAds(ads, KEY_ADS_POSITION.INARTICLE_1) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_1} data={ads} />}
              {article?.detail_1 && <div className='content-video' dangerouslySetInnerHTML={{ __html: article?.detail_1 }} />}
              {isExistAds(ads, KEY_ADS_POSITION.INARTICLE_2) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_2} data={ads} />}

              {!isEmpty(lastNews) && (
                <div className='mb-30'>
                  {article?.detail_1 ? (
                    <>
                      <LineLink lineContent={1} />
                      <div className='heading-related'>
                        <h2>
                          <span>ข่าววิดีโอ</span>ที่เกี่ยวข้อง
                        </h2>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='playlist-head mb-10'>
                        <div className='playlist-head-icon'>
                          <BsCaretRight />
                        </div>
                        <h2>ข่าววิดีโอที่เกี่ยวข้อง</h2>
                        <span className='videos-number'>
                          จำนวน <span> {lastNews?.length} </span> วิดีโอ
                        </span>
                      </div>
                    </>
                  )}

                  <div className='video-playlists playlist-custom-scroll --horizontal'>
                    <ul>
                      {lastNews.map((item, index) => (
                        <li className='m-0' key={index}>
                          <a href={item.link}>
                            <CardFull data={item} />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className='col-12 col-lg-4 colume-right'>
              <MediaQuery minWidth={1024}>
                <div className='video-player --small'>
                  {lastNews?.length > 0 &&
                    lastNews.slice(0, 1).map((item, index) => (
                      <a href={item.link} key={index}>
                        <CardFull data={item} />
                      </a>
                    ))}
                </div>

                <div>
                  <div className='playlist-head mb-10'>
                    <div className='playlist-head-icon'>
                      <BsCaretRight />
                    </div>
                    <h2>ข่าววิดีโอล่าสุด</h2>
                    <span className='videos-number'>
                      จำนวน <span> {lastNews?.length} </span> วิดีโอ
                    </span>
                  </div>
                  <div className='video-playlists playlist-custom-scroll --vertical'>
                    <ul>
                      {lastNews?.length > 0 &&
                        lastNews.slice(1).map((item, index) => (
                          <li className='w-100' key={index}>
                            <a href={item.link}>
                              <CardHVideo data={item} number={index + 1} />
                            </a>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </MediaQuery>
              <MediaQuery maxWidth={1023}>
                <div>
                  <div className='playlist-head last-news  mb-10'>
                    <div className='playlist-head-icon'>
                      <BsCaretRight />
                    </div>
                    <h2>ข่าววิดีโอล่าสุด</h2>
                    <span className='videos-number'>
                      <span> {lastNews?.length} </span> วิดีโอ
                    </span>
                  </div>
                  <div className='video-playlists playlist-custom-scroll --vertical'>
                    <ul>
                      {lastNews?.length > 0 &&
                        lastNews.map((item, index) => (
                          <li className='w-100' key={index}>
                            <a href={item.link}>
                              <CardHVideo data={item} number={index + 1} />
                            </a>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </MediaQuery>
              {isExistAds(ads, KEY_ADS_POSITION.STICKY_1) && <DynamicAds position={KEY_ADS_POSITION.STICKY_1} data={ads} />}
            </div>
          </div>
        </CategoryContent>
        {isExistAds(ads, KEY_ADS_POSITION.AD1X1) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
        {isExistAds(ads, KEY_ADS_POSITION.POSTARTICLE) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
      </Layout>
    </>
  );
};

const CategoryContent = styled.div`
  .content-video {
    font-family: 'Sarabun', sans-serif;
  }

  .heading-related {
    display: flex;
    align-items: center;
    width: 100%;
    margin: 30px 0 5px 0;
    border-bottom: 1px solid;
    h2,
    span {
      font-family: 'Sarabun', sans-serif;
      font-size: 20px !important;
      line-height: 1.75;
      margin: 0;
      color: rgba(0, 2, 28, 9.5);
    }
    span {
      font-weight: 900;
      color: rgba(0, 170, 44);
      padding-right: 3px;
    }
  }
  h2 {
    margin: 0;
  }
  ul {
    list-style: none;
    margin: 0;
  }
  .custom-socail {
    display: flex;
    ul {
      display: flex;
      padding: 0;
      li {
        padding: 2px;
      }
    }
  }
  .colume-right {
    .card-video-full {
      width: 100%;
      max-width: 100%;
    }
  }
  .article-video-player {
    position: relative;
    height: 576px;
    iframe {
      width: 100%;
      height: 100%;
    }
    @media (max-width: 854px) {
      height: 480px;
    }
    @media (max-width: 640px) {
      height: 360px;
    }
    @media (max-width: 425px) {
      height: 239px;
    }
  }

  .content-heading-wrapper {
    margin-bottom: 15px;
    @media (max-width: 475px) {
      min-height: 180px;
    }
    .content-title {
      font-size: 38px;
      font-family: 'prompt', sans-serif !important;
      line-height: 1.4;
      padding: 15px 0 8px 0;
      margin: 0;
      font-weight: 500;
      -webkit-line-clamp: initial;
    }

    .content-category {
      font-size: 14px;
      padding: 3px 10px;
      background: #01308b;
      font-weight: 500;
      color: #fff;
      border-radius: 3px;
    }

    .content-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      span {
        font-size: 12px;
        color: #a3a3a3;
        margin: 5px 8px 0 0;
        padding-right: 8px;
        font-weight: 400;
        /* border-right: 1px solid #dedede; */
        line-height: 14px;
        display: inline-block;

        &:last-child {
          margin-right: 0;
          border-right: none;
        }

        svg {
          vertical-align: top;
          margin-right: 3px;
        }
      }
      .views {
        margin-left: -5px;
        padding-left: 5px;
      }
    }
    .custom-socail {
      align-items: center;
    }
  }

  .content-video {
    margin: 0 0 15px 0;
  }

  .card-video-full {
    .video-duration {
      font-size: 11px;
      color: #00ff57 !important;
    }
  }
  .video-player,
  .video-div {
    position: relative;
    height: 434px;
    min-height: 210px; // 250px
    padding: 0;
    @media (max-width: 1024px) and (min-width: 769px) {
      min-height: 180px;
    }
    &.--small {
      height: 210px; // 250px
      @media (max-width: 1024px) and (min-width: 769px) {
        height: 180px;
      }
      .card-video-full {
        .video-duration {
          font-size: 11px;
          color: #00ff57 !important;
        }
        .img-scale-animate {
          border-radius: 3px 3px 0 0;
        }
      }
      @media (max-width: 768px) {
        height: 434px;
        min-height: 210px;
      }
      @media (max-width: 480px) {
        height: 250px;
        min-height: 210px;
      }
    }
    iframe {
      width: 100%;
      height: 100%;
      max-height: 434px;
      border-radius: 3px 3px 0 0;
      @media (max-width: 475px) {
        border-radius: 3px 3px 0 0;
      }
    }

    @media (max-width: 767px) {
      height: 250px;
    }
  }

  .playlist-head {
    background: #00ff57;
    color: #000;
    height: 60px;
    width: 100%;
    padding: 0 15px;
    line-height: 14px;
    z-index: 9;
    border-radius: 0 0 3px 3px;
    &.last-news {
      @media (max-width: 475px) {
        border-radius: 3px;
      }
    }

    h2 {
      padding-top: 12px;
      font-size: 18px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.2;
    }

    .videos-number {
      font-size: 12px;
      color: #0000009b;
    }

    .playlist-head-icon {
      font-size: 35px;
      float: left;
      margin-right: 10px;
      height: 60px;
      line-height: 55px;
      width: 40px;
      text-align: center;
      font-weight: bold;
      svg {
        vertical-align: middle;
      }
    }
  }
  .mb-10 {
    margin-bottom: 10px;
  }
  .video-playlists {
    position: relative;
    &.--horizontal {
      li:nth-child(1) {
        padding-left: 0;
      }
    }
    ul {
      padding: 0;
      li {
        display: inline-block;
        list-style-type: none;
        margin-right: 8px;
        cursor: pointer;
        padding: 5px;

        &.active {
          background-image: linear-gradient(to right, rgb(223 223 223 / 36%) 100%, #ffffff 100%) !important;
          h2 {
            color: rgba(0, 170, 44) !important;
          }
          .video-number {
            color: rgba(0, 170, 44) !important;
          }
        }
        &:hover {
          background-color: rgb(223 223 223 / 15%);
          h2 {
            color: rgba(0, 170, 44);
          }
          .video-number {
            color: rgba(0, 170, 44);
          }
        }
      }
    }
  }

  .social {
    display: flex;
    list-style-type: none;
    align-items: center;
    padding: 0;
    @media (max-width: 768px) {
      justify-content: center;
    }
    svg {
      circle {
        transition: all 0.5s;
      }
      &:hover {
        circle {
          fill: rgb(0, 0, 0);
        }
        path {
          fill: #00ff57;
        }
      }
    }
    li:not(:first-child) {
      padding-left: 5px;
    }
  }
`;

export async function getServerSideProps({ req, res, params, query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    article: null,
    related: null,
    lastNews: null,
    ads: null
  };

  try {
    const _uri = `/api/v1.0/video/contents/${params.id}`;
    const resData = await GET(_uri, false, true);
    if (isEmpty(resData?.data)) initProps.article = null;
    if (!isEmpty(resData?.data)) {
      initProps.article = resData.data;
      initProps.related = resData.related;
      initProps.lastNews = resData.last_news;
      initProps.ads = resData.ads;
    }
  } catch (err) {
    console.log('==========> ARTICLE DETAIL ERROR', err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }
  // console.log('vdo-content-data', initProps.article);
  return {
    props: initProps
  };
}

export default PageVideoDetail;
